import { Auth } from "aws-amplify";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { z } from "zod";
import InputField from "../../_shared/components/InputField/InputField";
import Loading from "../../_shared/components/Loading/Loading";
import SelectField from "../../_shared/components/SelectField/SelectField";
import { selectAreaCodes } from "../../_shared/constants/areaCodes";
import brokerages from "../../_shared/constants/brokerages";
import { useModal } from "../../_shared/contexts/ModalContext";
import { useUserDataQuery } from "../../_shared/graphql";
import NotificationPreferences from "./components/NotificationPreferences/NotificationPreferences";
import ProfileModal from "./components/ProfileModal/ProfileModal";
import ProfileRow from "./components/ProfileRow/ProfileRow";
import { VerifyPhoneBadge } from "./components/VerifyPhoneBadge/VerifyPhoneBadge";
import { ProfileForm } from "./models/ProfileForm";
import "./Profile.scss";

type UserProfile = {
  firstName: string;
  lastName: string;
  email: string;
  areaCode: string;
  phoneNumber: string;
  zip: string;
  brokerage: string;
};

Modal.setAppElement("#root");

export default function Profile() {
  const [profileForm, setProfileForm] = useState<ProfileForm | null>(null);
  const [userDetails, setUserDetails] = useState<UserProfile>();
  const [phoneVerified, setPhoneVerified] = useState(false);

  const { onErrorMsg, onSuccessMsg } = useModal();
  const { data: userData, refetch: refetchUserData } = useUserDataQuery();

  React.useEffect(() => {
    reloadUser();
  }, []); // eslint-disable-line

  useEffect(() => {
    setPhoneVerified(!!userData?.users[0].verified_sms);
  }, [userData?.users]);

  // TODO: this could come from our auth context
  // Authenticate current session to reload customer account details.
  const reloadUser = async () => {
    const user = (await Auth.currentSession()).getIdToken().payload;
    setUserDetails({
      firstName: user["custom:firstName"],
      lastName: user["custom:lastName"],
      email: user["email"],
      areaCode: user["custom:areaCode"],
      phoneNumber: user["custom:phoneNumber"],
      zip: user["custom:zip"],
      brokerage: user["custom:brokerage"],
    });
    refetchUserData();
  };

  // update password attribute
  const updatePassword = async ({ currentPassword, newPassword }: any) => {
    try {
      const userInfo = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(userInfo, currentPassword, newPassword);
      onSuccessMsg(`Your password was successfully changed.`);
      await reloadUser();
    } catch (err: any) {
      onErrorMsg(err.message);
    }
  };

  useEffect(() => {
    // prevent scrolling when modal is open
    if (!!profileForm) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "scroll";
  }, [profileForm]);

  if (!userDetails) return <Loading variant="inner" />;
  return (
    <>
      <div className="account-content">
        <div
          className="profile"
          style={{
            background: "#171717",
            padding: 30,
            borderRadius: 7,
            width: "60%",
            maxWidth: 1000,
          }}
        >
          <div className="table">
            <div className="profile-text">Profile</div>
            <ProfileRow
              label="Name"
              value={`${userDetails?.firstName} ${userDetails?.lastName}`}
              onEdit={() => {
                setProfileForm({
                  label: "Name",
                  fields: [
                    {
                      label: "First name",
                      name: "custom:firstName",
                      placeholder: userDetails.firstName,
                    },
                    {
                      label: "Last name",
                      name: "custom:lastName",
                      placeholder: userDetails.lastName,
                    },
                  ],
                });
              }}
            />
            <ProfileRow
              label="Email Address"
              value={userDetails?.email}
              disabledTooltip="To request an email change, please open a ticket under Help."
              onEdit={() => {
                /** // TODO: modify email address */
              }}
            />
            <ProfileRow
              label="Password"
              value={"***********"}
              onEdit={() => {
                setProfileForm({
                  label: "Password",
                  fields: [
                    {
                      label: "Current password",
                      name: "currentPassword",
                    },
                    {
                      label: "New password",
                      name: "newPassword",
                    },
                    {
                      label: "Confirm new password",
                      name: "confirmNewPassword",
                    },
                  ],
                  customSchema: z
                    .object({
                      currentPassword: z.string().min(1, {
                        message: "Please enter your current password.",
                      }),
                      newPassword: z.string().min(6, {
                        message:
                          "Make sure your new password is at least 6 characters long.",
                      }),
                      confirmNewPassword: z.string(),
                    })
                    .superRefine(({ newPassword, confirmNewPassword }, ctx) => {
                      if (newPassword !== confirmNewPassword)
                        ctx.addIssue({
                          code: "custom",
                          message: "Make sure your passwords match",
                          path: ["confirmNewPassword"],
                        });
                    }),
                  customInputs: (
                    <>
                      <InputField
                        type="password"
                        dark
                        name="currentPassword"
                        placeholder="Old password"
                      />
                      <InputField
                        type="password"
                        dark
                        name="newPassword"
                        placeholder="New password"
                      />
                      <InputField
                        type="password"
                        dark
                        name="confirmNewPassword"
                        placeholder="Confirm new password"
                      />
                    </>
                  ),
                  customOnSubmit: (values) => {
                    updatePassword(values);
                  },
                });
              }}
            />
            <ProfileRow
              label="Mobile Number"
              value={`${userDetails?.areaCode} ${userDetails?.phoneNumber}`}
              // TODO: ensure unchanged areaCode sends original
              onEdit={() => {
                setProfileForm({
                  label: "Mobile Number",
                  // fields: ["custom:areaCode", "custom:phoneNummber"],
                  fields: [
                    {
                      name: "custom:areaCode",
                      label: "Area code",
                      initialValue: userDetails.areaCode,
                    },
                    {
                      name: "custom:phoneNumber",
                      label: "Phone number",
                    },
                  ],
                  customInputs: (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 6,
                      }}
                    >
                      <SelectField
                        dark
                        name="custom:areaCode"
                        options={selectAreaCodes}
                      />
                      <InputField
                        dark
                        name="custom:phoneNumber"
                        label={userDetails.phoneNumber}
                      />
                    </div>
                  ),
                });
              }}
            />
            <VerifyPhoneBadge
              isVerified={phoneVerified}
              setProfileForm={setProfileForm}
            />

            <ProfileRow
              label="Zip Code"
              value={userDetails?.zip}
              onEdit={() => {
                setProfileForm({
                  label: "Zip Code",
                  fields: [
                    {
                      label: "Zip code",
                      name: "zip",
                      placeholder: userDetails.zip,
                    },
                  ],
                });
              }}
            />
            <ProfileRow
              label="Brokerage/Trading Account"
              value={userDetails?.brokerage}
              onEdit={() => {
                setProfileForm({
                  label: "Brokerage/Trading Account",
                  fields: [
                    {
                      label: "Brokerage",
                      name: "custom:brokerage",
                      initialValue: userDetails.brokerage,
                    },
                  ],
                  customInputs: (
                    <>
                      <SelectField
                        dark
                        name="custom:brokerage"
                        options={brokerages}
                      />
                    </>
                  ),
                  customSchema: z.object({
                    "custom:brokerage": z
                      .string()
                      .default(userDetails.brokerage),
                  }),
                });
              }}
            />

            <NotificationPreferences />
          </div>
        </div>
      </div>
      {profileForm && (
        <ProfileModal
          isOpen={!!profileForm}
          onClose={() => setProfileForm(null)}
          reloadUser={reloadUser}
          {...profileForm}
        />
      )}
    </>
  );
}
